import {Injectable} from '@angular/core';
import {BehaviorSubject, EMPTY, forkJoin, Observable, Subject} from 'rxjs';
import {Layer, LayerType} from './layer';
import {debounceTime, distinctUntilChanged, filter, finalize, map, skip, takeUntil, tap} from 'rxjs/operators';
import {GeospatialViewerService} from '../api/geospatial-viewer.service';
import {
    FindGeospatialOutagesRequest,
    FindGeospatialOutagesResponse,
    FindGeospatialEntitiesRequest,
    FindGeospatialEntitiesResponse,
} from '../model/api';
import {Unsubscribable} from '@core/interfaces/unsubscribable';
import {GeospatialViewerControlsStore} from '../api/geospatial-viewer-controls.store';
import {FormBuilder} from '@angular/forms';
import {GeospatialFilterStore} from '../api/geospatial-filter.store';
import {UsersStore} from '@store/common/users.store';
import {StudiesStore} from '@store/common/studies.store';
import {GeospatialEntitiesConfiguration, GeospatialViewerConfiguration} from '../model/viewer-config';
import {CustomMapLayer} from '../model/layers';
import {OutageLayer} from './outage-layer';
import {MetricLayer} from './metric-layer';
import {CustomLayer} from './custom-layer';
import {ProjectLayer} from './project-layer';
import {SystemLayer} from './system-layer';

@Injectable()
export class LayersService extends Unsubscribable {
    // Separate Observables for each type of layer
    private systemLayers: BehaviorSubject<SystemLayer[]> = new BehaviorSubject<SystemLayer[]>([
        new SystemLayer(this.fb, this.geospatialFilterStore),
    ]);
    systemLayers$: Observable<SystemLayer[]> = this.systemLayers.asObservable();

    private projectLayers: BehaviorSubject<ProjectLayer[]> = new BehaviorSubject<ProjectLayer[]>([
        new ProjectLayer(this.fb, this.geospatialFilterStore, this.userStore, this.studiesStore),
    ]);

    private _metricLayers: BehaviorSubject<MetricLayer[]> = new BehaviorSubject<MetricLayer[]>([
        this.createLayer(LayerType.METRIC) as MetricLayer,
    ]);
    metricLayers$: Observable<MetricLayer[]> = this._metricLayers.asObservable();

    private _outageLayers: BehaviorSubject<OutageLayer[]> = new BehaviorSubject<OutageLayer[]>([
        this.createLayer(LayerType.OUTAGE) as OutageLayer,
    ]);
    outageLayers$: Observable<OutageLayer[]> = this._outageLayers.asObservable();

    private _customLayers: BehaviorSubject<CustomLayer[]> = new BehaviorSubject<CustomLayer[]>([]);
    customLayers$: Observable<CustomLayer[]> = this._customLayers.asObservable();

    // Subject that will emit changes for the specific layer that needs to be re-rendered
    private _outageLayerRerender: BehaviorSubject<Layer | null> = new BehaviorSubject<Layer | null>(null);
    outageLayerRerender$: Observable<Layer | null> = this._outageLayerRerender.asObservable();

    // Subject that will indicate the completion of layer queue re-rendering
    private _outageLayersRerenderComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    outageLayersRerenderComplete$: Observable<boolean> = this._outageLayersRerenderComplete.asObservable();

    // Subject that will emit changes for the specific layer that needs to be re-rendered
    private _metricLayerRerender: BehaviorSubject<Layer | null> = new BehaviorSubject<Layer | null>(null);
    metricLayerRerender$: Observable<Layer | null> = this._metricLayerRerender.asObservable();

    // Subject that will indicate the completion of layer queue re-rendering
    private _metricLayersRerenderComplete: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    metricLayersRerenderComplete$: Observable<boolean> = this._metricLayersRerenderComplete.asObservable();

    private cancelRequest$ = new Subject<void>();
    private initConfigComplete: boolean = false;
    private minZoomRendering: number = 13;

    constructor(
        private geospatialViewerService: GeospatialViewerService,
        private geospatialViewerControlsStore: GeospatialViewerControlsStore,
        private fb: FormBuilder,
        private geospatialFilterStore: GeospatialFilterStore,
        private userStore: UsersStore,
        private studiesStore: StudiesStore,
    ) {
        super();

        this.geospatialViewerControlsStore.config$
            .pipe(
                takeUntil(this.unsubscribe$),
                distinctUntilChanged(),
                filter((r) => !!r),
                skip(1),
                debounceTime(500),
            )
            .subscribe((config: GeospatialViewerConfiguration) => {
                this.refreshAllAssetLayers();
                // Run this only once
                if (!this.initConfigComplete) {
                    this.initCustomMapLayers(config);
                    this.initZoomLevelCutoff(config);
                    this.initConfigComplete = true;
                }
            });

        this.geospatialViewerControlsStore.currentStudyId$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
            this.resetToDefaultMetricLayer();
            this.resetToDefaultOutageLayer();
        });
    }

    get projectLayer(): ProjectLayer {
        return this.projectLayers.value[0];
    }

    get metricLayers(): MetricLayer[] {
        return this._metricLayers.value;
    }

    get outageLayers(): OutageLayer[] {
        return this._outageLayers.value;
    }

    get firstMetricLayer(): MetricLayer {
        return this._metricLayers.value[0];
    }

    getLayerGroup(layer: Layer): BehaviorSubject<Layer[]> {
        switch (layer.type) {
            case LayerType.METRIC:
                return this._metricLayers;
            case LayerType.OUTAGE:
                return this._outageLayers;
        }
    }

    getLayerRerender(layer: Layer): BehaviorSubject<Layer> {
        switch (layer.type) {
            case LayerType.METRIC:
                return this._metricLayerRerender;
            case LayerType.OUTAGE:
                return this._outageLayerRerender;
        }
    }

    createLayer(layerType: LayerType): Layer {
        switch (layerType) {
            case LayerType.OUTAGE:
                return new OutageLayer(this.fb, this.geospatialFilterStore);
            case LayerType.METRIC:
                return new MetricLayer(this.fb, this.geospatialFilterStore);
        }
    }

    duplicateLayer(layer: Layer) {
        const newLayer: Layer = this.createLayer(layer.type);
        newLayer.copy(layer);

        // Add the duplicated layer to the list
        this.addLayer(newLayer);

        // Return the new layer instance
        return newLayer;
    }

    addLayer(layer: Layer) {
        const layers = this.getLayerGroup(layer);
        const currentLayers = layers.getValue();
        this.closeLayers(currentLayers);
        layers.next([...currentLayers, layer]);
    }

    removeLayer(layer: Layer) {
        const layers = this.getLayerGroup(layer);
        layer.visible = false;
        this.rerenderLayer(layer);

        const currentLayers = layers.getValue().filter((_layer) => _layer.id !== layer.id);
        layers.next(currentLayers);
    }

    rerenderLayer(layer: Layer) {
        this.getLayerRerender(layer).next(layer); // Emit the specific layer that was updated
    }

    createOutageLayer(): OutageLayer {
        return new OutageLayer(this.fb, this.geospatialFilterStore);
    }

    createMetricLayer(): MetricLayer {
        return new MetricLayer(this.fb, this.geospatialFilterStore);
    }

    closeLayers(currentLayers: Layer[]) {
        currentLayers.forEach((layer) => layer.closeLayer());
    }

    resetToDefaultMetricLayer() {
        const currentMetricLayer = this._metricLayers.getValue();

        currentMetricLayer.forEach((layer: MetricLayer) => {
            layer.visible = false;
            this.rerenderLayer(layer);
        });

        this._metricLayers.next([this.createLayer(LayerType.METRIC) as MetricLayer]);
    }

    resetToDefaultOutageLayer() {
        const currentOutageLayer = this._outageLayers.getValue();

        currentOutageLayer.forEach((layer: OutageLayer) => {
            layer.visible = false;
            this.rerenderLayer(layer);
        });

        this._outageLayers.next([this.createLayer(LayerType.OUTAGE) as OutageLayer]);
    }

    toggleLayerVisibility(layer: Layer) {
        // Do not propagate changes for any CustomLayer - currently no data is ever available.
        if (layer && !(layer instanceof CustomLayer)) {
            layer.toggleVisibility();

            // Trigger re-rendering of the map
            if (layer instanceof MetricLayer) {
                layer.visible ? this.refreshMetricLayerData(layer) : this.rerenderLayer(layer);

                this._metricLayers.next(this._metricLayers.getValue());
            }
            if (layer instanceof OutageLayer) {
                layer.visible ? this.refreshOutageLayerData(layer) : this.rerenderLayer(layer);

                this._outageLayers.next(this._outageLayers.getValue());
            }
        }
    }

    public refreshMetricLayerData(layer: MetricLayer) {
        if (layer.layerForm.valid && layer.layerForm.value.visible) {
            this.getMapGeoData(layer).subscribe();
        }
    }

    public refreshOutageLayerData(layer: OutageLayer) {
        if (layer.layerForm.valid && layer.layerForm.value.visible) {
            this.getMapOutageGeoData(layer).subscribe();
        }
    }

    /** Refresh metric and outage layers */
    public refreshAllAssetLayers(): void {
        this.refreshMetricLayers();
        this.refreshOutageLayers();
    }

    private refreshMetricLayers(): void {
        const refreshRequestsMetric = this._metricLayers.value
            .filter((layer: MetricLayer) => layer.layerForm.valid && layer.layerForm.value.visible)
            .map((layer: MetricLayer) => this.getMapGeoData(layer));
        if (refreshRequestsMetric.length > 0) {
            forkJoin(refreshRequestsMetric).subscribe(() => {
                //Trigger complete map re-rendering after all layers in the queue have been processed
                this._metricLayersRerenderComplete.next(true);
            });
        }
    }

    private refreshOutageLayers(): void {
        const refreshRequestsOutage = this._outageLayers.value
            .filter((layer: OutageLayer) => layer.layerForm.valid && layer.layerForm.value.visible)
            .map((layer: OutageLayer) => this.getMapOutageGeoData(layer));
        if (refreshRequestsOutage.length > 0) {
            forkJoin(refreshRequestsOutage).subscribe(() => {
                //Trigger complete map re-rendering after all layers in the queue have been processed
                this._outageLayersRerenderComplete.next(true);
            });
        }
    }

    cancelRequest() {
        this.cancelRequest$.next();
        this.cancelRequest$.complete();
        this.cancelRequest$ = new Subject<void>();
    }

    /** Get the outage layer data and supply it to the map layer to be consumed by arcgis */
    public getMapOutageGeoData(layer: OutageLayer): Observable<FindGeospatialOutagesResponse> {
        this.cancelRequest();
        const centerConfig = this.geospatialViewerControlsStore.getConfig()?.centerCoordinate;

        let req: FindGeospatialOutagesRequest = {
            visualizationType: layer.getOutageViewBy(),
            labelFormat: layer.getLabelFormat(),
            metric: layer.getOutageMetric(),
            filterList: layer.getFilters(),
            buckets: layer.getBuckets(),
            centerConfig: centerConfig,
        };

        layer.setLoading(true);

        if (centerConfig.zoomLevel >= this.minZoomRendering) {
            layer.visible = true;
            return this.geospatialViewerService.getMapOutageGeoEntities(req).pipe(
                debounceTime(1500),
                takeUntil(this.cancelRequest$),
                finalize(() => {
                    layer.setLoading(false);
                }),
                tap((mapDataSource: FindGeospatialOutagesResponse) => {
                    layer.updateMapDataSource(mapDataSource);

                    this.rerenderLayer(layer); // Re-render the map

                    this._outageLayers.next(this._outageLayers.getValue());
                }),
            );
        } else {
            // Clear data from all metric layers, do not fetch new data
            layer.clearMapDataSource();

            layer.setLoading(false);
            this.rerenderLayer(layer);
            return EMPTY;
        }
    }

    public getMapGeoData(layer: MetricLayer): Observable<FindGeospatialEntitiesResponse> {
        this.cancelRequest();
        const centerConfig = this.geospatialViewerControlsStore.getConfig()?.centerCoordinate;

        let req: FindGeospatialEntitiesRequest = {
            visualizationType: layer.getVisualizationType(),
            measure: layer.getMeasure(),
            metric: layer.getMetric(),
            filterList: layer.getFilters(),
            buckets: layer.getBuckets(),
            centerConfig,
        };

        layer.setLoading(true);

        /*
         * Clear all metric layer content when zoomed out further than zoomLevelCutoff (default: 13)
         * - Above (i.e., zoomed in): refresh data as normal
         * - Below (i.e., zoomed out): clear data from all metric layers, do not fetch new data
         */
        if (centerConfig.zoomLevel >= this.minZoomRendering) {
            layer.visible = true;
            return this.geospatialViewerService
                .getMapGeoEntities(this.geospatialViewerControlsStore.getCurrentStudyId(), req)
                .pipe(
                    debounceTime(1500),
                    takeUntil(this.cancelRequest$),
                    finalize(() => {
                        layer.setLoading(false);
                    }),
                    tap((mapDataSource: GeospatialEntitiesConfiguration) => {
                        layer.updateMapDataSource(mapDataSource);
                        layer.updateRenderedVisualizationType(mapDataSource.visualizationType);

                        this.rerenderLayer(layer); // Re-render the map

                        this._metricLayers.next(this._metricLayers.getValue());
                    }),
                );
        } else {
            // Clear data from all metric layers, do not fetch new data
            layer.clearMapDataSource();

            layer.setLoading(false);
            this.rerenderLayer(layer);
            return EMPTY;
        }
    }

    /*
     * Support for custom map layers
     */
    public initCustomMapLayers(config: GeospatialViewerConfiguration): void {
        if (config?.customLayers?.length > 0) {
            this._customLayers.next(
                config.customLayers.map((l: CustomMapLayer) => new CustomLayer(this.fb, this.geospatialFilterStore, l)),
            );
        }
    }

    /*
     * Support for zoom level cut-off
     */
    public initZoomLevelCutoff(config: GeospatialViewerConfiguration): void {
        if (config?.centerCoordinate?.minZoomRendering) {
            this.minZoomRendering = config.centerCoordinate.minZoomRendering;
        }
    }
}
